import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../../../apiClient';
import {
    CreateOrganizationSportGameCapacityOptionDto,
    GetOrganizationSportGameCapacityOptionsDto,
    OrganizationSportGameCapacityOptionDto,
    UpdateOrganizationSportGameCapacityOptionDto
} from '../../../models/organization/dto/organization-sport-game-capacity-option.dto';

const QUERY_KEY = 'organization-game-capacity-options';

export const useGetOrganizationGameCapacityOptions = (
  organizationId: string,
  filters?: Omit<GetOrganizationSportGameCapacityOptionsDto, 'organizationId'>,
) => {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: [QUERY_KEY, organizationId, filters],
    queryFn: () => {
      const params = new URLSearchParams({
        organizationId,
        ...(filters?.sport && { sport: filters.sport }),
      });
      return apiClient.fetchApi<OrganizationSportGameCapacityOptionDto[], {}>({
        method: 'GET',
        endpoint: `organization-sport-game-capacity-options?${params.toString()}`,
      });
    },
    enabled: !!organizationId,
  });
};

/**
 * The API's create response: the option, plus what saving it did to what
 * players can actually book.
 *
 * `retiredCapacities` is the one that has no analogue on the other rails.
 * Configuring the FIRST capacity option for a sport moves the organization from
 * "nothing configured" to "these are the capacities", and the value installed
 * app builds that predate the capacity picker send is not carried over — so a
 * save the operator reads as purely additive silently stops those builds being
 * able to pay, one screen after a quote that succeeded. The API cannot widen
 * the accepted set back without re-opening a deliberate narrowing, so it
 * reports instead, and the remedy is one click: add that capacity as an option
 * too.
 *
 * `unpricedCapacities` names capacities that ARE bookable afterwards but that
 * no court prices — the booking is accepted and the price lookup then 404s.
 *
 * All three are optional for the same reason `strandedPricingRows` is: an API
 * that predates them sends nothing, and `?? []` / `?? 0` at the call site reads
 * that as "no warning".
 */
export type CreateCapacityOptionResponse = OrganizationSportGameCapacityOptionDto & {
  strandedPricingRows?: number;
  retiredCapacities?: number[];
  unpricedCapacities?: number[];
};

export const useCreateOrganizationGameCapacityOption = (
  organizationId: string,
  onSuccess?: (result: CreateCapacityOptionResponse) => void,
) => {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Omit<CreateOrganizationSportGameCapacityOptionDto, 'organizationId'>) =>
      apiClient.fetchApi<CreateCapacityOptionResponse, CreateOrganizationSportGameCapacityOptionDto>({
        method: 'POST',
        endpoint: 'organization-sport-game-capacity-options',
        body: {
          ...data,
          organizationId,
        },
      }),
    onSuccess: (result) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY, organizationId],
      });
      onSuccess?.(result);
    },
  });
};

/**
 * The API's update response: the option, plus how many pricing rows this edit
 * left unreachable. Re-pointing an option's capacity retires the old one exactly
 * as deleting it does, so the field means the same thing on both rails. Optional
 * for the same reason as the delete's — an API that predates it sends nothing,
 * and `?? 0` at the call site reads that as "no warning".
 */
export type UpdateCapacityOptionResponse = OrganizationSportGameCapacityOptionDto & {
  strandedPricingRows?: number;
  /**
   * The mirror image of `strandedPricingRows`: capacities that are bookable
   * after this edit but that no court prices. Re-pointing an option to a
   * capacity nobody has priced yet is exactly how a picker comes to offer a
   * choice whose quote 404s.
   */
  unpricedCapacities?: number[];
};

export const useUpdateOrganizationGameCapacityOption = (
  organizationId: string,
  onSuccess?: (result: UpdateCapacityOptionResponse) => void,
) => {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateOrganizationSportGameCapacityOptionDto }) =>
      apiClient.fetchApi<UpdateCapacityOptionResponse, UpdateOrganizationSportGameCapacityOptionDto>({
        method: 'PUT',
        endpoint: `organization-sport-game-capacity-options/${id}`,
        body: data,
      }),
    onSuccess: (result) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY, organizationId],
      });
      onSuccess?.(result);
    },
  });
};

/**
 * The API's delete response. `strandedPricingRows` is how many pricing rows the
 * retirement leaves unreachable — courts that are now unpriced at the capacity
 * just removed. Optional because an API that predates the field sends nothing;
 * `?? 0` at the call site reads that as "no warning", which is exactly the
 * behaviour dashboards had before it existed.
 */
export type DeleteCapacityOptionResponse = {
  message: string;
  strandedPricingRows?: number;
  /**
   * Capacities that are bookable after the delete but that no court prices.
   * A delete can newly expose one: removing the LAST option puts the sport back
   * on the unconfigured fallback, which accepts a wider set than the configured
   * one did.
   */
  unpricedCapacities?: number[];
};

export const useDeleteOrganizationGameCapacityOption = (
  organizationId: string,
  onSuccess?: (result: DeleteCapacityOptionResponse) => void,
) => {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      apiClient.fetchApi<DeleteCapacityOptionResponse, {}>({
        method: 'DELETE',
        endpoint: `organization-sport-game-capacity-options/${id}`,
      }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY, organizationId],
      });
      onSuccess?.(data);
    },
  });
};

export const useGetAvailableCapacities = (organizationId: string, sport: string) => {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: ['available-capacities', organizationId, sport],
    queryFn: () =>
      apiClient.fetchApi<number[], {}>({
        method: 'GET',
        endpoint: `organization-sport-game-capacity-options/available-capacities/${organizationId}/${sport}`,
      }),
    enabled: !!organizationId && !!sport,
  });
};

export const useGetDefaultCapacityForSport = (organizationId: string, sport: string) => {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: ['default-capacity', organizationId, sport],
    queryFn: () =>
      apiClient.fetchApi<OrganizationSportGameCapacityOptionDto | null, {}>({
        method: 'GET',
        endpoint: `organization-sport-game-capacity-options/default-capacity/${organizationId}/${sport}`,
      }),
    enabled: !!organizationId && !!sport,
  });
};
